import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { Route, Play, MapPin } from 'lucide-react';
import { DraggablePanel } from '../common/DraggablePanel';
import { cn } from '../../utils/cn';

interface DistancePanelProps {
  onClose: () => void;
  startPoint: [number, number] | null;
  endPoint: [number, number] | null;
  selectingPoint: 'start' | 'end' | null;
  onSelectStart: () => void;
  onSelectEnd: () => void;
  onCalculate: (modes: { walk: boolean; drive: boolean; bird: boolean }) => void;
  results: {
    walkDistance?: string;
    walkTime?: string;
    driveDistance?: string;
    driveTime?: string;
    birdDistance?: string;
  } | null;
}

export const DistancePanel: React.FC<DistancePanelProps> = ({
  onClose,
  startPoint,
  endPoint,
  selectingPoint,
  onSelectStart,
  onSelectEnd,
  onCalculate,
  results,
}) => {
  const { t } = useTranslation();
  const [walkMode, setWalkMode] = useState(true);
  const [driveMode, setDriveMode] = useState(true);
  const [birdMode, setBirdMode] = useState(true);

  return (
    <DraggablePanel
      title={t('distance.title')}
      onClose={onClose}
      icon={<Route size={14} />}
      width={300}
    >
      <div className="p-3 space-y-3">
        {/* Point selectors */}
        <div className="space-y-2">
          <button
            onClick={onSelectStart}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-xs rounded border transition-colors',
              selectingPoint === 'start'
                ? 'border-gn-success bg-gn-success-light text-gn-success'
                : 'border-gn-light hover:bg-gn-tertiary text-gn-primary',
            )}
          >
            <MapPin size={14} className="text-gn-success" />
            {startPoint
              ? t('distance.start_at', { lat: startPoint[1].toFixed(5), lon: startPoint[0].toFixed(5) })
              : t('distance.set_start')}
          </button>

          <button
            onClick={onSelectEnd}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 text-xs rounded border transition-colors',
              selectingPoint === 'end'
                ? 'border-gn-danger bg-gn-danger-light text-gn-danger'
                : 'border-gn-light hover:bg-gn-tertiary text-gn-primary',
            )}
          >
            <MapPin size={14} className="text-gn-danger" />
            {endPoint
              ? t('distance.end_at', { lat: endPoint[1].toFixed(5), lon: endPoint[0].toFixed(5) })
              : t('distance.set_end')}
          </button>
        </div>

        {/* Mode toggles */}
        <div className="space-y-1">
          <label className="flex items-center gap-2 text-xs text-gn-primary">
            <input type="checkbox" checked={walkMode} onChange={(e) => setWalkMode(e.target.checked)} className="rounded accent-blue-500" />
            {t('distance.walking')}
          </label>
          <label className="flex items-center gap-2 text-xs text-gn-primary">
            <input type="checkbox" checked={driveMode} onChange={(e) => setDriveMode(e.target.checked)} className="rounded accent-blue-500" />
            {t('distance.driving')}
          </label>
          <label className="flex items-center gap-2 text-xs text-gn-primary">
            <input type="checkbox" checked={birdMode} onChange={(e) => setBirdMode(e.target.checked)} className="rounded accent-blue-500" />
            {t('distance.bird')}
          </label>
        </div>

        <button
          onClick={() => onCalculate({ walk: walkMode, drive: driveMode, bird: birdMode })}
          disabled={!startPoint || !endPoint}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded text-xs font-medium transition-colors',
            !startPoint || !endPoint
              ? 'bg-gn-tertiary text-gn-secondary cursor-not-allowed'
              : 'bg-gn-blue text-gn-inverse hover:bg-gn-blue',
          )}
        >
          <Play size={12} /> {t('distance.calculate')}
        </button>

        {/* Results */}
        {results && (
          <div className="bg-gn-secondary rounded p-3 space-y-1.5 border border-gn-light">
            <h4 className="text-[10px] font-semibold text-gn-secondary uppercase">{t('analysis.results')}</h4>
            {results.walkDistance && (
              <div className="flex justify-between text-xs">
                <span className="text-gn-secondary">{t('analysis.walking')}</span>
                <span className="font-medium text-gn-primary">{results.walkDistance} {results.walkTime && `(${results.walkTime})`}</span>
              </div>
            )}
            {results.driveDistance && (
              <div className="flex justify-between text-xs">
                <span className="text-gn-secondary">{t('analysis.driving')}</span>
                <span className="font-medium text-gn-primary">{results.driveDistance} {results.driveTime && `(${results.driveTime})`}</span>
              </div>
            )}
            {results.birdDistance && (
              <div className="flex justify-between text-xs">
                <span className="text-gn-secondary">{t('distance.bird_short')}</span>
                <span className="font-medium text-gn-primary">{results.birdDistance}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </DraggablePanel>
  );
};
